// client/src/world/flora/clutter.ts
/** Vertex-colored ground cover and deadfall, with bounded shared templates
 * for the near band. Low leaf thickets use a denser mesh than loose clutter. */
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { rngFromString, range, type PlanetDef, type Rng } from "@hyperlight/shared";
import { V, tube, blob, paint, flat, shade } from "./primitives";
import type { C3 } from "./types";
import { buildFern } from "./forestForms";
import { landscapeColors } from "../landscapePalette";
import { buildShrub, buildCanyonShrub, buildMoss, buildLitter } from './floorForms';

function merged(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const g = mergeGeometries(parts, false);
  if (!g) throw new Error("clutter merge failed");
  for (const p of parts) p.dispose();
  g.computeVertexNormals();
  return g;
}

export function buildGrassTuft(rng: Rng, leaf: C3): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  // Folded leaf ribbons sharing a grounded base. No cone silhouettes: a
  // meadow tussock fans outward and its tips bend, unlike miniature saplings.
  const blades=12+Math.floor(rng()*6);
  for(let i=0;i<blades;i++){
    const angle=rng()*Math.PI*2,h=range(rng,.55,1.1),w=range(rng,.055,.105);
    const x=range(rng,-.36,.36),z=range(rng,-.36,.36),lean=range(rng,.22,.6);
    const side=new THREE.Vector3(Math.cos(angle),0,Math.sin(angle));
    const front=new THREE.Vector3(-side.z,0,side.x);
    const root=V(x,0,z),mid=root.clone().add(V(0,h*.58,0)).addScaledVector(front,lean*.25);
    const tip=root.clone().add(V(0,h,0)).addScaledVector(front,lean);
    const left=mid.clone().addScaledVector(side,-w),right=mid.clone().addScaledVector(side,w);
    const ridge=mid.clone().addScaledVector(front,-w*.35);
    const g=new THREE.BufferGeometry();
    g.setAttribute('position',new THREE.Float32BufferAttribute([
      ...root.toArray(),...left.toArray(),...ridge.toArray(),
      ...root.toArray(),...ridge.toArray(),...right.toArray(),
      ...left.toArray(),...tip.toArray(),...ridge.toArray(),
      ...ridge.toArray(),...tip.toArray(),...right.toArray(),
    ],3));
    parts.push(paint(g,shade(leaf,range(rng,.86,1.13)),1));
  }
  return merged(parts);
}

export function buildStump(rng: Rng, bark: C3): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  // Sized to read as the base of a REAL tree, not a fence post.
  const r = range(rng, 0.45, 0.8), h = range(rng, 0.5, 1.0);
  const body = flat(new THREE.CylinderGeometry(r * 0.92, r, h, 6));
  body.translate(0, h / 2, 0);
  parts.push(paint(body, bark));
  const top = flat(new THREE.CylinderGeometry(r * 0.86, r * 0.86, 0.04, 6));
  top.translate(0, h, 0);
  parts.push(paint(top, shade([bark[0] + 0.25, bark[1] + 0.2, bark[2] + 0.15], 1)));
  const roots = 2 + Math.floor(rng() * 3);
  for (let i = 0; i < roots; i++) {
    const a = rng() * Math.PI * 2;
    parts.push(tube(V(Math.cos(a) * r * 0.6, 0.12, Math.sin(a) * r * 0.6), V(Math.cos(a) * r * 1.7, 0, Math.sin(a) * r * 1.7), 0.1, 0.05, bark, 4));
  }
  return merged(parts);
}

export function buildLog(rng: Rng, bark: C3): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  // Fallen TRUNKS, not sticks — matches the standing trees' girth and height.
  const r = range(rng, 0.38, 0.65), len = range(rng, 4.0, 8.0);
  parts.push(tube(V(-len / 2, r, 0), V(len / 2, r * 0.9, 0), r, r * 0.85, bark, 6));
  // Recessed heartwood and a darker inner ring make the ends read as timber.
  const wood: C3 = [bark[0] + .19, bark[1] + .14, bark[2] + .075];
  for (const side of [-1, 1]) {
    const x = side * len / 2, y = side < 0 ? r : r * .9, radius = r * (side < 0 ? .88 : .73);
    parts.push(tube(V(x, y, 0), V(x + side * .012, y, 0), radius, radius, wood, 6));
    parts.push(tube(V(x + side * .013, y, 0), V(x + side * .016, y, 0), radius * .53, radius * .53, shade(wood, .85), 6));
  }
  if (rng() < 0.7) {
    const bx = range(rng, -0.3, 0.3) * len;
    parts.push(tube(V(bx, r, 0), V(bx + range(rng, 0.5, 1.1), r + range(rng, 0.5, 1.0), range(rng, -0.6, 0.6)), r * 0.35, r * 0.15, bark, 4));
  }
  return merged(parts);
}

const FLOWER_HEADS: C3[] = [[0.95, 0.4, 0.4], [0.98, 0.85, 0.3], [0.6, 0.5, 0.95], [0.97, 0.97, 0.97]];

export function buildFlower(rng: Rng, leaf: C3): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const stems = 1 + Math.floor(rng() * 3);
  for (let i = 0; i < stems; i++) {
    const x = range(rng, -0.2, 0.2), z = range(rng, -0.2, 0.2);
    const h = range(rng, 0.25, 0.45);
    parts.push(tube(V(x, 0, z), V(x, h, z), 0.02, 0.015, shade(leaf, 0.9), 4, 0.5));
    parts.push(blob(rng, V(x, h + 0.06, z), range(rng, 0.08, 0.14), FLOWER_HEADS[Math.floor(rng() * FLOWER_HEADS.length)], 0.15, 0, 1));
  }
  return merged(parts);
}

const CAP_COLORS: C3[] = [[0.78, 0.24, 0.18], [0.62, 0.42, 0.24], [0.85, 0.75, 0.55]];

export function buildMushroom(rng: Rng): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const shrooms = 1 + Math.floor(rng() * 3);
  for (let i = 0; i < shrooms; i++) {
    const x = range(rng, -0.2, 0.2), z = range(rng, -0.2, 0.2);
    const h = range(rng, 0.12, 0.25), r = range(rng, 0.05, 0.09);
    const stem = flat(new THREE.CylinderGeometry(r, r * 1.2, h, 5));
    stem.translate(x, h / 2, z);
    parts.push(paint(stem, [0.9, 0.87, 0.78]));
    const cap = flat(new THREE.SphereGeometry(range(rng, 0.12, 0.2), 6, 4));
    cap.scale(1, 0.55, 1);
    cap.translate(x, h, z);
    parts.push(paint(cap, CAP_COLORS[Math.floor(rng() * CAP_COLORS.length)]));
  }
  return merged(parts);
}

export interface ClutterSets {
  shrub: THREE.BufferGeometry[];
  shrubCoarse: THREE.BufferGeometry[];
  moss: THREE.BufferGeometry[];
  litter: THREE.BufferGeometry[];
  fern: THREE.BufferGeometry[];
  grass: THREE.BufferGeometry[];
  stump: THREE.BufferGeometry[];
  log: THREE.BufferGeometry[];
  flower: THREE.BufferGeometry[];
  mushroom: THREE.BufferGeometry[];
  /** Created only when a tile actually contains canyon refuge plants. */
  canyonShrub?: THREE.BufferGeometry[];
  canyonShrubCoarse?: THREE.BufferGeometry[];
  canyonGrass?: THREE.BufferGeometry[];
}

/** The owning planet already releases every template array in ClutterSets. */
export function canyonClutter(def:PlanetDef,sets:ClutterSets) {
  const four=(kind:string,build:(rng:Rng)=>THREE.BufferGeometry)=>Array.from({length:4},(_,i)=>build(rngFromString(`${def.seed}:canyon-clutter:${kind}:${i}`)));
  sets.canyonShrub??=four('scrub',r=>buildCanyonShrub(r,[.23,.24,.10]));
  sets.canyonShrubCoarse??=four('scrub',r=>buildCanyonShrub(r,[.23,.24,.10],true));
  sets.canyonGrass??=four('tuft',r=>buildGrassTuft(r,[.32,.29,.13]));
  return {shrub:sets.canyonShrub,shrubCoarse:sets.canyonShrubCoarse,grass:sets.canyonGrass};
}

/** 4 deterministic variants per kind, sharing the landscape's foliage family. */
export function buildClutter(def: PlanetDef): ClutterSets {
  const sc = landscapeColors(def).foliage;
  const leaf: C3 = [sc[0], sc[1], sc[2]];
  const bark: C3 = [0.34, 0.25, 0.17];
  const four = (kind: string, f: (rng: Rng) => THREE.BufferGeometry): THREE.BufferGeometry[] => {
    const out: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 4; i++) out.push(f(rngFromString(`${def.seed}:clutter:${kind}:${i}`)));
    return out;
  };
  return {
    shrub: four('shrub', r => buildShrub(r, shade(leaf,.72))),
    shrubCoarse: four('shrub', r => buildShrub(r, shade(leaf,.72),true)),
    moss: four('moss', r => buildMoss(r, shade(leaf,.66))),
    litter: four('litter', r => buildLitter(r)),
    fern: four("fern", (r) => buildFern(r, shade(leaf, .85))),
    grass: four("grass", (r) => buildGrassTuft(r, landscapeColors(def).ground.table[7])),
    stump: four("stump", (r) => buildStump(r, bark)),
    log: four("log", (r) => buildLog(r, bark)),
    flower: four("flower", (r) => buildFlower(r, leaf)),
    mushroom: four("mushroom", (r) => buildMushroom(r)),
  };
}
